// The intake attendance dialog, opened from the intake call by V1's *Record as held* (views/Intake.tsx). BRD 3.4 and
// Build Spec 3.1: "HM + Recruiter required; HRBP optional." (CR-018). One CheckTile per party; the Hiring Manager and
// the Recruiter carry *Required*, the HR Business Partner *HRBP optional*. Leaving the HR Business Partner unticked asks
// for nothing; leaving the Hiring Manager or the Recruiter unticked opens the *Reason* the server requires for the
// exception (intake.record_held keeps V1's exception path for them alone), and *Record* stays dashed until it is given.
// The server decides: a refusal is the red banner at the top of the body with his rule, the ticks kept (DC-24).

import { useState } from "react";
import { AppError } from "@/api/runtime";
import { Banner, CheckTile, COMMON_LABELS, CORE_LABELS, DIALOG_LABELS, Dialog, ErrorStrip, Field, G1_LABELS, Pill, ROLE_LABELS, Stack, TextArea } from "@/mc";
import { HELD_WORDS } from "./labels";

export type HeldSeat = "hm" | "recruiter" | "hrbp";

/** One party of the call: the requisition's seat holder (the HR Business Partner is optional). */
export interface HeldAttendee {
  person_id: string;
  name: string;
  seat: HeldSeat;
}

/** What intake.record_held takes from the dialog. */
export interface HeldInput {
  attendance: { person_id: string; present: boolean }[];
  absence_exception_reason: string | null;
}

export interface RecordHeldProps {
  open: boolean;
  attendees: readonly HeldAttendee[];
  /** Records the call (intake.record_held); a rejection is the server's refusal. */
  record: (input: HeldInput) => Promise<unknown>;
  onClose: () => void;
  /** The call is recorded (the caller opens the interview plan). */
  onRecorded?: (() => void) | undefined;
  /** A gallery or unit-test rendering: in place, non-modal. */
  preview?: boolean | undefined;
  /** The ticks the dialog opens with (default: every party present, as V1 opened it). */
  initialPresent?: Partial<Record<HeldSeat, boolean>> | undefined;
}

const SEAT_WORD: Readonly<Record<HeldSeat, string>> = { hm: ROLE_LABELS.hm, recruiter: ROLE_LABELS.recruiter, hrbp: ROLE_LABELS.hrbp };
const REQUIRED: ReadonlySet<HeldSeat> = new Set(["hm", "recruiter"]);

/** The parties left unticked that the rule requires (the Hiring Manager, the Recruiter): they need the exception. */
export function absentRequired(attendees: readonly HeldAttendee[], present: Readonly<Record<string, boolean>>): HeldAttendee[] {
  return attendees.filter((attendee) => REQUIRED.has(attendee.seat) && present[attendee.person_id] === false);
}

/** The server's answer, or a lost connection as the runtime names one (status 0). */
function asProblem(error: unknown): AppError {
  if (error instanceof AppError) return error;
  return new AppError({ type: "about:blank", title: "", status: 0, detail: String(error), refusal_code: "NETWORK" });
}

export function RecordHeld({ open, attendees, record, onClose, onRecorded, preview, initialPresent }: RecordHeldProps) {
  const [present, setPresent] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(attendees.map((attendee) => [attendee.person_id, initialPresent?.[attendee.seat] ?? true])),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<AppError | null>(null);
  const isPresent = (attendee: HeldAttendee) => present[attendee.person_id] ?? true;
  const exception = absentRequired(attendees, Object.fromEntries(attendees.map((a) => [a.person_id, isPresent(a)]))).length > 0;
  const missingReason = exception && reason.trim().length === 0;
  const refused = failure !== null && failure.status >= 400 && failure.status < 500;

  const submit = () => {
    if (busy || missingReason) return;
    setBusy(true);
    setFailure(null);
    record({
      attendance: attendees.map((attendee) => ({ person_id: attendee.person_id, present: isPresent(attendee) })),
      absence_exception_reason: exception ? reason.trim() : null,
    })
      .then(() => onRecorded?.())
      .catch((error: unknown) => setFailure(asProblem(error)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog
      title={HELD_WORDS.intakeAttendance}
      open={open}
      preview={preview}
      onClose={() => onClose()}
      dismissLabel={DIALOG_LABELS.cancel}
      hint={HELD_WORDS.hmRecruiterRequired}
      refused={refused ? { reason: HELD_WORDS.hmRecruiterRequired } : undefined}
      primary={{
        label: HELD_WORDS.record,
        onClick: submit,
        busy,
        blocked: missingReason ? { reason: HELD_WORDS.hmRecruiterRequired } : undefined,
        hs: "plan.held.record",
      }}
    >
      <Stack gap={10}>
        {attendees.map((attendee) => (
          <CheckTile
            key={attendee.person_id}
            checked={isPresent(attendee)}
            onChange={(checked) => setPresent((was) => ({ ...was, [attendee.person_id]: checked }))}
            sub={SEAT_WORD[attendee.seat]}
            right={REQUIRED.has(attendee.seat) ? <Pill tone="warn">{COMMON_LABELS.required}</Pill> : <Pill tone="neutral">{HELD_WORDS.hrbpOptional}</Pill>}
            
          >
            {attendee.name}
          </CheckTile>
        ))}
        {exception ? (
          <Field
            label={G1_LABELS.reason}
            required
            hint={G1_LABELS.reasonStored}
            error={failure?.fieldErrors.absence_exception_reason ? G1_LABELS.reason : undefined}
          >
            <TextArea rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)}  />
          </Field>
        ) : null}
        {failure !== null && !refused ? <ErrorStrip message={CORE_LABELS.syncFailure} onRetry={submit} problem={{ status: failure.status, refusal_code: failure.code, detail: failure.message, request_id: failure.requestId }} /> : null}
        {failure === null && attendees.length === 0 ? <Banner tone="warn">{HELD_WORDS.hmRecruiterRequired}</Banner> : null}
      </Stack>
    </Dialog>
  );
}
